import { Subject } from "rxjs";
import { Injectable, signal } from "@angular/core";
import { withParamCache } from "../plugins/opsctrl-foc/param-cache";

export interface FlightPreparation {
  flightId: string;
  flightNumber: string;
  origin: string;
  destination: string;
  status: "pending" | "ready" | "failed";
  message: string;
}

/** Owns saved-flight preparation across page navigation. */
@Injectable({ providedIn: "root" })
export class FlightPreparationService {
  readonly updated = new Subject<string>();
  readonly calculationLabels = ["Fuel", "Mass & balance", "Performance"];
  readonly flights = signal<FlightPreparation[]>([]);
  readonly labels = ["Route", "Weather / NOTAM", "Fuel", "Mass & balance", "Performance"];

  get busy(): boolean {
    return this.flights().some((flight) => flight.status === "pending");
  }

  has(flightId: string): boolean {
    return this.flights().some((flight) => flight.flightId === flightId);
  }

  get(flightId: string): FlightPreparation | undefined {
    return this.flights().find((flight) => flight.flightId === flightId);
  }

  publish(flightId: string): void {
    this.updated.next(flightId);
  }

  acknowledgeReady(flightId: string): void {
    this.flights.update((flights) =>
      flights.filter((flight) => flight.flightId !== flightId || flight.status !== "ready")
    );
  }

  progress(flightId: string, message: string): void {
    this.flights.update((flights) =>
      flights.map((flight) => (flight.flightId === flightId ? { ...flight, message } : flight))
    );
  }

  start(summary: Omit<FlightPreparation, "status" | "message">, prepare: () => Promise<void>): Promise<void> {
    if (this.busy) throw new Error("Another flight briefing is still being prepared.");
    this.flights.update((flights) => [
      ...flights.filter((flight) => flight.flightId !== summary.flightId),
      { ...summary, status: "pending", message: "Preparing briefing..." },
    ]);
    // One read cache for the whole preparation: its steps ask the same questions.
    return Promise.resolve()
      .then(() => withParamCache(prepare))
      .then(() => {
        this.flights.update((flights) =>
          flights.map((flight) =>
            flight.flightId === summary.flightId ? { ...flight, status: "ready", message: "Briefing ready" } : flight
          )
        );
        this.publish(summary.flightId);
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        this.flights.update((flights) =>
          flights.map((flight) =>
            flight.flightId === summary.flightId ? { ...flight, status: "failed", message } : flight
          )
        );
        this.publish(summary.flightId);
        console.error("Flight briefing preparation failed", error);
      });
  }
}
